/**
 * CashFlowHeaderKPIs — 4 cartes au-dessus du graphique cash-flow.
 *
 * Cartes :
 *   1. Solde actuel              — somme bank-accounts (authorized_balance_cents)
 *   2. Solde fin de période      — balance_end_cents du dernier bucket de la fenêtre
 *                                  (= 31/12 si toggle "Année en cours", today+12m si "M±12")
 *   3. Charges prévi du mois     — charges_forecast_cents (mois courant) — = exactement la
 *                                  valeur "Charges Prévisionnel" du widget Point mort
 *                                  + sous-ligne "déjà dépensé ce mois" (charges_realised_cents)
 *   4. Revenus perçus            — revenues_received_cents (mois courant) — = "Généré" du
 *                                  widget Point mort
 *
 * Toutes les valeurs du mois courant utilisent le même hook que la HomePage
 * (useMonthSnapshot) pour garantir la cohérence des chiffres entre les 2 pages.
 *
 * Props :
 *   buckets : array from /v1/cash-flow response
 *   loading : boolean — chargement des buckets
 */

import { TrendingUp, Banknote, TrendingDown } from 'lucide-react';
import { formatCurrency } from '../lib/format.js';
import { splitByPurpose } from '../lib/bankAccounts.js';
import LoadingSpinner from './LoadingSpinner.jsx';
import { useBankAccounts } from '../hooks/useBankAccounts.js';
import { useMonthSnapshot } from '../hooks/useMonthSnapshot.js';

function formatShortDate(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

const MONTH_FMT = new Intl.DateTimeFormat('fr-FR', { month: 'long' });
function currentMonthLabel() {
  return MONTH_FMT.format(new Date());
}

function KpiCard({ label, value, sub, icon: Icon, accentClass = '', loading = false, compact = false, children }) {
  return (
    <div className={`rounded-xl bg-white border border-slate-200 shadow-sm flex flex-col ${compact ? 'px-3 py-3 gap-1' : 'px-4 py-4 gap-2'}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
        {Icon && <Icon size={16} className="text-slate-400" aria-hidden />}
      </div>
      {loading ? (
        <LoadingSpinner size="sm" className="my-1" />
      ) : (
        <p className={`${compact ? 'text-lg' : 'text-2xl'} font-bold tabular-nums ${accentClass || 'text-slate-900'}`}>
          {value ?? '—'}
        </p>
      )}
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
      {children}
    </div>
  );
}

export default function CashFlowHeaderKPIs({ buckets = [], loading = false, compact = false }) {
  // ── Solde actuel (bank-accounts) ────────────────────────────────────────────
  const { data: accountsData, isLoading: accountsLoading } = useBankAccounts();
  const accounts = Array.isArray(accountsData) ? accountsData : [];
  // Trésorerie DISPONIBLE : cohérent avec la courbe du tableau, qui exclut
  // désormais les sinking funds (et leurs transactions) — cf. migration 0068.
  const { operatingCents, sinkingCents, sinkingFunds } = splitByPurpose(accounts);
  const currentBalance = accounts.length ? operatingCents : null;

  // ── Solde fin de période (dynamique, suit la fenêtre buckets) ──────────────
  const lastBucket = buckets.length > 0 ? buckets[buckets.length - 1] : null;
  const endBalance = lastBucket?.balance_end_cents ?? null;
  const endDate    = lastBucket?.period_end ?? null;

  // ── Snapshot du mois courant (= source de vérité partagée avec le Point mort) ─
  const { data: snapshot, isLoading: snapshotLoading } = useMonthSnapshot();
  const chargesForecastCents  = Number(snapshot?.charges_forecast_cents  ?? 0);
  const chargesRealisedCents  = Number(snapshot?.charges_realised_cents  ?? 0);
  const revenuesReceivedCents = Number(snapshot?.revenues_received_cents ?? 0);

  return (
    <div className={`grid ${compact ? 'grid-cols-2 gap-3' : 'grid-cols-1 gap-4'} md:grid-cols-2 lg:grid-cols-4`}>
      {/* 1. Solde actuel */}
      <KpiCard
        label="Solde actuel"
        value={currentBalance !== null ? formatCurrency(currentBalance) : '—'}
        sub={sinkingFunds.length
          ? `Disponible · ${formatCurrency(sinkingCents)} provisionnés à part`
          : 'Solde bancaire temps réel'}
        icon={TrendingUp}
        loading={accountsLoading}
        compact={compact}
      />

      {/* 2. Solde fin de période — dynamique */}
      <KpiCard
        label="Solde fin de période"
        value={endBalance !== null ? formatCurrency(endBalance) : '—'}
        sub={endDate ? `Au ${formatShortDate(endDate)}` : 'Aucune projection disponible'}
        icon={TrendingUp}
        loading={loading}
        compact={compact}
      />

      {/* 3. Charges prévi du mois (= Point mort, "Charges Prévisionnel") */}
      <KpiCard
        label="Charges prévi du mois"
        value={`-${formatCurrency(chargesForecastCents)}`}
        icon={TrendingDown}
        loading={snapshotLoading}
        compact={compact}
      >
        <p className="text-xs text-slate-500 tabular-nums">
          Dont déjà dépensé :{' '}
          <span className="font-medium text-slate-700">
            {snapshotLoading ? '…' : `-${formatCurrency(chargesRealisedCents)}`}
          </span>
        </p>
      </KpiCard>

      {/* 4. Revenus perçus (= Point mort, "Généré") */}
      <KpiCard
        label="Revenus perçus"
        value={`+${formatCurrency(revenuesReceivedCents)}`}
        sub={`Depuis le 1er ${currentMonthLabel()}`}
        icon={Banknote}
        loading={snapshotLoading}
        compact={compact}
      />
    </div>
  );
}
